import { alpha, type SxProps, type Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";

import { isDarkMode } from "@/src/theme/helpers";
import { radius, spacing } from "@/src/theme/tokens";

/**
 * Fill for the checked chip.
 *
 * Dark mode Success/Selected falls back to the 8 % hover overlay, which is
 * indistinguishable from Background/Paper, so the checked state reads a
 * stronger Success/Main tint instead.
 */
const checkedFillColor = (muiTheme: Theme): string =>
  isDarkMode(muiTheme)
    ? alpha(muiTheme.palette.semantic.success.main, 0.16)
    : muiTheme.palette.semantic.success.selected;

export const getFilterChipRootSx = (
  isChecked: boolean,
  sx?: SxProps<Theme>
): SxProps<Theme> => [
  (muiTheme: Theme): SystemStyleObject<Theme> => ({
    height: [40, 36],
    minHeight: [40, 36],
    display: "inline-flex",
    alignItems: "center",
    borderRadius: radius.xs,
    cursor: "pointer",
    border: "1px solid",
    borderColor: isChecked
      ? muiTheme.palette.semantic.success.main
      : muiTheme.palette.misc.outlineBorder,
    backgroundColor: isChecked
      ? checkedFillColor(muiTheme)
      : muiTheme.palette.background.paper,
    transition: "all 0.3s ease",
    px: 1.5,
    py: [spacing.xs / 2, 0],
    "&:hover": {
      backgroundColor: isChecked
        ? muiTheme.palette.semantic.success.focus
        : muiTheme.palette.action.hover
    },
    "&:active": {
      transform: "scale(0.98)"
    }
  }),
  ...(Array.isArray(sx) ? sx : sx ? [sx] : [])
];

export const getFilterChipIconSx =
  (hideLabelOnMobile: boolean): SxProps<Theme> =>
  (muiTheme) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "20px",
    width: "20px",
    height: "20px",
    flexShrink: 0,
    mr: hideLabelOnMobile ? [0, spacing.sm] : spacing.sm,
    color: muiTheme.palette.text.secondary,
    "& > svg": {
      fontSize: "20px",
      width: "20px",
      height: "20px",
      display: "block",
      margin: "auto"
    }
  });

export const getFilterChipLabelSx =
  (isChecked: boolean, hideLabelOnMobile: boolean): SxProps<Theme> =>
  (muiTheme) => ({
    display: hideLabelOnMobile ? ["none", "block"] : "block",
    color: muiTheme.palette.text.primary,
    fontWeight: isChecked
      ? muiTheme.typography.fontWeightBold
      : muiTheme.typography.fontWeightRegular,
    lineHeight: 1.5
  });
